import type { CartBox } from '../order/cartStorage'
import type { ConfirmationSnapshot } from '../checkout/confirmationState'
import {
  buildCommerceState,
  type CommerceState,
} from '../../shared/ecommerceTracking'
import {
  getAnalyticsConsent,
  startAnalytics,
  trackAnalyticsEvent,
} from './analytics'
import { trackMetaEvent, trackMetaPurchase, type MetaPurchaseQueueState } from './metaPixel'

const purchaseMemoryKeys = new Set<string>()
const viewContentKeys = new Set<string>()

function gaItems(state: CommerceState) {
  return state.products.map((product) => ({
    item_id: product.id,
    item_name: product.name,
    item_category: product.category,
    quantity: product.quantity,
  }))
}

function metaParameters(state: CommerceState) {
  return {
    content_type: 'product',
    content_ids: state.products.map((product) => product.id),
    contents: state.products.map((product) => ({
      id: product.id,
      quantity: product.quantity,
    })),
    currency: state.currency,
    value: state.value,
    num_items: state.numItems,
  }
}

function sendGaEvent(
  eventName: 'add_to_cart' | 'begin_checkout' | 'purchase',
  parameters: Record<string, unknown>,
): boolean {
  if (getAnalyticsConsent() !== 'granted') return false
  if (!startAnalytics()) return false
  return trackAnalyticsEvent(eventName, parameters)
}

function purchaseStorageKey(provider: 'ga4' | 'meta', orderId: string) {
  return `sg.ecommerce.purchase.v1:${provider}:${orderId}`
}

function purchaseAlreadySent(provider: 'ga4' | 'meta', orderId: string): boolean {
  const key = purchaseStorageKey(provider, orderId)
  if (purchaseMemoryKeys.has(key)) return true
  try {
    return window.sessionStorage.getItem(key) === 'sent'
  } catch {
    return false
  }
}

function markPurchaseSent(provider: 'ga4' | 'meta', orderId: string): void {
  const key = purchaseStorageKey(provider, orderId)
  purchaseMemoryKeys.add(key)
  try {
    window.sessionStorage.setItem(key, 'sent')
  } catch {
    // In-memory duplicate protection still applies for this document.
  }
}

export function trackAddToCartEvents(box: CartBox, cartBoxCount?: number): {
  ga4: boolean
  meta: boolean
} {
  const state = buildCommerceState([box])

  const ga4 = sendGaEvent('add_to_cart', {
    currency: state.currency,
    value: state.value,
    box_size: box.quantity,
    box_price: box.price,
    ...(typeof cartBoxCount === 'number' ? { cart_box_count: cartBoxCount } : {}),
    items: gaItems(state),
  })

  const meta = trackMetaEvent('AddToCart', metaParameters(state))
  return { ga4, meta }
}

export function trackBeginCheckoutEvents(boxes: readonly CartBox[]): {
  ga4: boolean
  meta: boolean
} {
  const state = buildCommerceState(boxes)

  const ga4 = sendGaEvent('begin_checkout', {
    currency: state.currency,
    value: state.value,
    items: gaItems(state),
  })

  const meta = trackMetaEvent('InitiateCheckout', metaParameters(state))
  return { ga4, meta }
}

export type BrowserPurchaseState = MetaPurchaseQueueState | 'duplicate' | 'not-applicable'

export function trackPaidPurchaseEvents(snapshot: ConfirmationSnapshot): {
  ga4: boolean
  meta: boolean
  metaState: BrowserPurchaseState
} {
  if (snapshot.purchaseSuppressed === true || snapshot.method !== 'card' || snapshot.paymentStatus !== 'paid') {
    return { ga4: false, meta: false, metaState: 'not-applicable' }
  }

  const finalValue = snapshot.amountCents / 100
  const state = buildCommerceState(snapshot.boxes, finalValue)

  let ga4 = false
  if (!purchaseAlreadySent('ga4', snapshot.orderId)) {
    ga4 = sendGaEvent('purchase', {
      transaction_id: snapshot.orderId,
      currency: state.currency,
      value: state.value,
      shipping: (snapshot.deliveryFeeCents ?? 0) / 100,
      items: gaItems(state),
    })
    if (ga4) markPurchaseSent('ga4', snapshot.orderId)
  }

  let meta = false
  let metaState: BrowserPurchaseState = 'duplicate'
  if (!purchaseAlreadySent('meta', snapshot.orderId)) {
    metaState = trackMetaPurchase(metaParameters(state), snapshot.orderId)
    meta = metaState === 'queued'
    if (meta) markPurchaseSent('meta', snapshot.orderId)
  }

  return { ga4, meta, metaState }
}

export function trackOrderViewContent(
  products: readonly {
    id: string
    name: string
    type: string
  }[],
): boolean {
  if (!products.length || typeof window === 'undefined') return false

  const key = `order-view:${window.location.pathname}`
  if (viewContentKeys.has(key)) return false

  const sent = trackMetaEvent('ViewContent', {
    content_type: 'product',
    content_name: 'Build Your Box',
    content_category: 'Weekly menu',
    content_ids: products.map((product) => product.id),
    contents: products.map((product) => ({
      id: product.id,
      quantity: 1,
    })),
  })
  if (sent) viewContentKeys.add(key)
  return sent
}
